import clsx from 'clsx';
import React, { useCallback } from 'react';
import CheckmarkIcon from '~/icons/checkmark.svg';
import ScaleIcon from '~/icons/scale.svg';
import { Typography } from '~/shared/components/Typography';
import { useIsMobile } from '~/shared/hooks/useIsMobile';
import {
    ComparisonItem,
    comparisonCopy,
    MAX_COMPARISON_ITEMS,
    useIsComparisonFull,
    useIsInComparison,
    useIsProductComparisonEnabled,
    useProductComparisonStore,
} from '~/shared/productComparison';
import styles from './CompareToggle.module.scss';

type Props = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
    item: ComparisonItem;
    /**
     * `overlay` (default) is the pill shown on a product card, which on mobile switches to
     * a checkbox + "Vælg" - the PDP's `ghost` usage stays the icon + "Sammenlign" look on
     * every viewport, since there's no grid of cards there to save space on.
     */
    variant?: 'overlay' | 'ghost';
};

/**
 * Adds or removes a product from the comparison.
 *
 * Owns the feature flag check so call sites can hand it a product without knowing whether
 * comparison is switched on, and reads its own selection state through narrow store selectors
 * so toggling one card does not re-render the rest of the grid.
 */
export const CompareToggle: React.FC<Props> = ({
    className,
    item,
    variant = 'overlay',
    ...rest
}) => {
    const isEnabled = useIsProductComparisonEnabled();
    const isSelected = useIsInComparison(item.id);
    const isFull = useIsComparisonFull();
    const itemsCount = useProductComparisonStore((state) => state.items.length);
    const toggle = useProductComparisonStore((state) => state.toggle);
    const setIsCollapsed = useProductComparisonStore((state) => state.setIsCollapsed);
    const isMobile = useIsMobile();

    const onClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            // The card is a link, and this button sits inside it.
            event.preventDefault();
            event.stopPropagation();

            const completesComparison = !isSelected && itemsCount + 1 >= MAX_COMPARISON_ITEMS;

            toggle(item);

            // `toggle` opens the tray expanded. On the PLP grid that covers the products the
            // customer is still browsing, so mobile collapses it straight back to the pill -
            // unless this pick completes the comparison, in which case it stays open to show
            // the full state. The PDP (`ghost` variant) has no grid behind it, so it always
            // stays expanded there.
            if (variant === 'overlay' && !isSelected && isMobile && !completesComparison) {
                setIsCollapsed(true);
            }
        },
        [variant, isSelected, isMobile, itemsCount, item, toggle, setIsCollapsed],
    );

    if (!isEnabled) return null;

    const isDisabled = isFull && !isSelected;

    return (
        <button
            type="button"
            className={clsx(styles.compareToggle, isSelected && styles.isSelected, className)}
            aria-pressed={isSelected}
            disabled={isDisabled}
            title={isDisabled ? comparisonCopy.maxReached : undefined}
            {...rest}
            onClick={onClick}
        >
            {variant === 'overlay' && (
                // Mobile shows a checkbox + "Vælg" instead of the scale icon + "Sammenlign" -
                // both are rendered and the breakpoint mixin picks one, so which shows never
                // depends on a client-side viewport check (no mount/resize flicker).
                <span className={styles.checkboxGroup}>
                    <span className={styles.checkbox} aria-hidden="true">
                        {isSelected && <CheckmarkIcon className={styles.checkboxIcon} />}
                    </span>
                    <Typography as="span" variant="paragraph-S">
                        {comparisonCopy.select}
                    </Typography>
                </span>
            )}

            <span className={clsx(styles.label, variant === 'overlay' && styles.iconGroup)}>
                <ScaleIcon className={styles.icon} aria-hidden="true" />
                <Typography as="span" variant="paragraph-S">
                    {comparisonCopy.add}
                </Typography>
            </span>
        </button>
    );
};
